import httpClient from 'api/core/httpClient';
import { joinUrl, lastPathSegment, PATCH_WITHOUT_DETAIL } from 'utils/urls';
import auth from './auth';

/**
 * URL에서 tenant/warehouse 파싱
 * - 기대 형식: /{tenant}/{warehouse}/...
 * - 일치하지 않으면 null 반환
 */
function parseTenantWarehouseFromLocation() {
  try {
    const { pathname } = window.location;
    const parts = pathname.split('/').filter(Boolean); // ['', 'tenant', 'warehouse', ...] -> ['tenant','warehouse',...]
    if (parts.length >= 2) {
      const [tenant, warehouse] = parts;
      if (tenant && warehouse) return { tenant, warehouse };
    }
  } catch {
    /* no-op */
  }
  return { tenant: null, warehouse: null };
}

class BaseApi {
  constructor(resource) {
    this.httpClient = httpClient;
    this.resource = resource;
  }

  /**
   * API basePath 규칙
   * - 일반: /c/{tenant}/{warehouse}/{resource}/
   * - me:   /c/{tenant}/{me}/ (warehouse 없음)
   * - auth: /c/{company}/{auth}/ (company 도메인 기반)
   */
  getResourceUrl() {
    const { tenant, warehouse } = parseTenantWarehouseFromLocation();

    // 1) AUTH 계열 (/auth)
    if (this.resource === 'auth') {
      const company = auth.getCompanyDomain();
      if (!company) {
        const e = new Error('AUTH_COMPANY_NOT_READY');
        e.code = 'AUTH_COMPANY_NOT_READY';
        throw e;
      }
      return `/p/${this.resource}/`;
    }

    // 2) ME 계열 (/me) — warehouse 없음
    if (this.resource === 'me') {
      // URL에서 tenant 있으면 우선 사용
      if (tenant) return `/p/${this.resource}/`;

      // URL에 tenant가 아직 없으면 company(public) 사용
      const company = auth.getCompanyDomain();
      if (company) return `p/${this.resource}/`;

      const e = new Error('CTX_NOT_READY_FOR_ME');
      e.code = 'CTX_NOT_READY_FOR_ME';
      throw e;
    }

    // 3) 일반 리소스 — tenant/warehouse 모두 필요
    if (!tenant || !warehouse) {
      const e = new Error('CTX_NOT_READY');
      e.code = 'CTX_NOT_READY';
      throw e;
    }

    return `/c/${tenant}/${warehouse}/${this.resource}/`;
  }

  getList(params = {}, config) {
    const q = new URLSearchParams(params).toString();
    const url = q ? `${this.getResourceUrl()}?${q}` : this.getResourceUrl();
    return config ? this.httpClient.get(url, config) : this.httpClient.get(url);
  }

  getById(id) {
    return this.httpClient.get(`${this.getResourceUrl()}${id}/`);
  }

  getLineById(id) {
    return this.httpClient.get(`${this.getResourceUrl()}${id}/items/`);
  }

  create(data) {
    return this.httpClient.post(this.getResourceUrl(), data);
  }

  createForm(data) {
    return this.httpClient.post(`${this.getResourceUrl()}bulk-action/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  update(id, data) {
    return this.httpClient.put(`${this.getResourceUrl()}${id}/`, data);
  }

  patch(id, data) {
    return this.httpClient.patch(`${this.getResourceUrl()}${id}/`, data);
  }

  patchForm(id, data) {
    const baseUrl = this.getResourceUrl();
    const resourceKey = (
      this.resourceKey || lastPathSegment(baseUrl)
    ).toLowerCase();

    const includeDetail = !PATCH_WITHOUT_DETAIL.has(resourceKey); // 기본 포함, 예외는 제외

    // /detail/ 포함/제외를 반영해 URL 생성 (+ 항상 끝에 / 보장)
    const url =
      (includeDetail
        ? joinUrl(baseUrl, 'detail', encodeURIComponent(String(id)))
        : joinUrl(baseUrl, encodeURIComponent(String(id)))) + '/';

    if (data instanceof FormData) {
      return this.httpClient.patch(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return this.httpClient.patch(url, data);
  }

  delete(id) {
    return this.httpClient.delete(`${this.getResourceUrl()}${id}/`);
  }
}

export default BaseApi;
